import { ref, type Ref } from 'vue'

// Owns the "fetch + try/catch + loaded/error/needsAuth" lifecycle that
// every Pinia store with a JSON endpoint duplicates today
// (`useWikiPagesStore.fetchAll`, `useLintNotesStore.fetchAll`,
// `useSourceActivityStore.load`).
//
// Caller passes a fetcher (returns a Promise of the parsed payload).
// The composable owns the data ref + lifecycle flags and exposes a
// `refresh()` to re-run the fetcher. 401 responses surface as
// `needsAuth = true` so the SPA's auth gate can flip without every
// store rolling its own check.
//
// Usage:
//
//   const { data: pages, loaded, error, needsAuth, refresh } =
//     useAsyncResource<WikiPage[]>(async () => {
//       const res = await fetch('/api/v1/wiki_pages', { credentials: 'include' })
//       if (res.status === 401) throw new UnauthorizedError()
//       if (!res.ok) throw new Error(`Failed: ${res.status}`)
//       return res.json()
//     })
//
//   await refresh()
//
// Stores are responsible for their own URL + parser; the composable
// stays type-agnostic so the same shape works for `WikiPage[]`,
// `{notes, openCount}`, or any other endpoint payload. The fetcher
// signals 401 by throwing a sentinel `UnauthorizedError`; everything
// else is a regular Error and lands in `error.value`.
//
// Pass `onError` to suppress re-throwing (stores that want best-effort
// loads without forcing callers to `.catch()`):
//
//   useAsyncResource(fetcher, { onError: () => {} })  // silent on failure
//   useAsyncResource(fetcher, { onError: e => reportError(e) }) // log + suppress

// Sentinel error type: stores throw this from their fetcher when the
// API responds 401. The composable catches it and flips `needsAuth`
// instead of `error`.
export class UnauthorizedError extends Error {
  constructor(message = 'Unauthorized') {
    super(message)
    this.name = 'UnauthorizedError'
  }
}

export interface AsyncResource<TData> {
  data: Ref<TData>
  loaded: Ref<boolean>
  // True for the duration of an in-flight `refresh()` (set at the top,
  // cleared in `finally`). Distinct from `loaded`, which only flips true
  // once and stays true: `loading && !loaded` is the "first load, nothing
  // to show yet" spinner condition, while `loading` alone drives a
  // refresh indicator on an already-populated view. Stores that don't
  // need it can simply not destructure it.
  loading: Ref<boolean>
  error: Ref<string | null>
  needsAuth: Ref<boolean>
  refresh: () => Promise<void>
}

export interface AsyncResourceOptions<T> {
  // Materialise `data` to this value before the first fetch resolves
  // so consumers (e.g. Pinia stores returning the ref as part of
  // their state) don't have to null-check every read, and so Pinia's
  // `$patch({ data: ... })` mutates a ref that already exists. When
  // supplied, the returned `data` ref is typed as `Ref<T>` instead of
  // `Ref<T | null>` — see the overload below.
  initialValue?: T
  // When provided, called with the Error instead of re-throwing from
  // `refresh()`. Stores that want best-effort loads (show stale data,
  // surface the error via `error.value`) pass `() => {}` to suppress
  // the throw so callers don't need a `.catch()` wrapper.
  onError?: (e: Error) => void
}

// Overloads: when `initialValue` is provided, the data ref is never
// null, so the ref type narrows to `Ref<T>` and callers don't need an
// `as unknown as Ref<T>` cast at the seam.
export function useAsyncResource<T>(
  fetcher: () => Promise<T>,
  options: { initialValue: T } & AsyncResourceOptions<T>,
): AsyncResource<T>
export function useAsyncResource<T>(
  fetcher: () => Promise<T>,
  options?: AsyncResourceOptions<T>,
): AsyncResource<T | null>
export function useAsyncResource<T>(
  fetcher: () => Promise<T>,
  options: AsyncResourceOptions<T> = {},
): AsyncResource<T | null> {
  const data = ref<T | null>(options.initialValue ?? null) as Ref<T | null>
  const loaded = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const needsAuth = ref(false)

  async function refresh(): Promise<void> {
    error.value = null
    loading.value = true
    try {
      data.value = await fetcher()
      needsAuth.value = false
      loaded.value = true
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        needsAuth.value = true
        loaded.value = true
        return
      }
      const err = e instanceof Error ? e : new Error('fetch failed')
      error.value = err.message
      loaded.value = true
      if (options.onError) {
        options.onError(err)
      } else {
        throw e
      }
    } finally {
      loading.value = false
    }
  }

  return { data, loaded, loading, error, needsAuth, refresh }
}
